import { createAction, props } from '@ngrx/store';
import { AddressEdit, AddressState } from '../address-collection.state';

/**
 * Address store actions.
 *
 * Payloads are restricted to the serializable `JsonAddressStateModel` shape (or
 * primitives) so they pass the root store's `strictActionSerializability` check.
 * Write/transformation logic that operates on `AddressModel` instances stays in
 * the `TimesheetAndExpensesNgrxService` facade; only the resulting collections
 * flow through these actions.
 *
 * `correlationId` on the save outcome actions lets the facade match a persisted
 * result back to the specific `saveAddressesRequested` it dispatched, so its
 * returned observable completes (or errors) for that write rather than for a
 * concurrent one.
 */

// --- Lifecycle ---------------------------------------------------------------

/** Dispatched by the facade's `init()` to start the storage-load effect. */
export const initAddresses = createAction('[Address] Init');

// --- Storage sync ------------------------------------------------------------

/** Dispatched by the load effect once the persisted collection has been read and sorted. */
export const addressesLoadedFromStorage = createAction(
    '[Address] Loaded from storage',
    props<{ addresses: AddressState[] }>(),
);

/**
 * Dispatched by the facade's `addAddressesAndSave` and the manual-entry sync
 * effect with a serialized collection of candidate addresses. The merge effect
 * folds them into the current collection — an unknown address is added to the
 * front, a known addrKey replaces its entry — and forwards the result to
 * `saveAddressesRequested` under the same `correlationId`; an unchanged
 * collection settles with `addressesUnchanged` instead.
 */
export const addAddressesRequested = createAction(
    '[Address] Add requested',
    props<{ addresses: AddressState[]; correlationId: string }>(),
);

/**
 * Folds a single address into the collection. The slice's general-purpose write: whoever holds an
 * address — a notification load, a hand-entered one, anything reached later — hands it over exactly
 * as its source produced it and the slice places it, appending an address it does not know and
 * updating the entry it already holds for that place (see `upsertAddress`). The upsert effect
 * normalizes the address first, so no caller has to know about the OData `<city>;<state>` encoding.
 *
 * Deliberately one address at a time: the caller usually needs to know which entry each of its
 * addresses ended up as, and a bulk write cannot answer that per address.
 */
export const upsertAddressRequested = createAction(
    '[Address] Upsert requested',
    props<{ address: AddressState; correlationId: string }>(),
);

/**
 * Dispatched by the upsert effect with the collection the upsert produced. The reducer sets it
 * straight away — the store is the source of truth, and a caller reading the slice right after
 * dispatching an upsert has to see it — while `persistUpsertedAddresses$` mirrors it to storage
 * once a burst of upserts has settled.
 */
export const addressUpserted = createAction(
    '[Address] Upserted',
    props<{ addresses: AddressState[]; correlationId: string }>(),
);

/** Dispatched by the merge effect when the candidate addresses changed nothing. */
export const addressesUnchanged = createAction('[Address] Unchanged', props<{ correlationId: string }>());

/**
 * Dispatched by `NotificationEffects.mergeAddresses` once per notification load, after its
 * per-notification upserts have all landed, with the ids of every address that load carried — every
 * notification's `addressSet` plus its `customerAddress`. The scope effect narrows the collection to
 * exactly those ids (and every user-created address, which is exempt): see
 * `markAddressesOutOfScope`. Ids rather than `addrNumber`s, because `resolveConvertedAddresses` has
 * already reconciled each fresh address to the identity the collection holds for it by the time this
 * is dispatched, so the id is the stable handle — an `addrNumber` still varies between a manual one
 * and whatever the backend later assigns.
 */
export const addressScopeRequested = createAction(
    '[Address] Scope requested',
    props<{ inScopeIds: string[]; correlationId: string }>(),
);

/**
 * Dispatched by the address picker's edit form with the id of the address to change and the fields
 * the form let the technician retype.
 *
 * Targets the address by `id` rather than by content, unlike `addAddressesRequested` (→
 * `mergeNewAddressesIntoCollection`) and `upsertAddressRequested` (→ `upsertAddress`), which both
 * locate their target through `findKnownAddress`'s content match (name1..name4, street, postCode,
 * city, country) for an address without a backend `addrNumber`. An edit changes that very content,
 * so matching on it would find no existing entry, append the edited address as a second one, and
 * leave the original standing unchanged — exactly the outcome an edit must not have.
 *
 * Everything an edit does not carry stays as it was: `id`, `addrKey`, `addrNumber`, `visible` and the
 * tombstone. Keeping the identity is what matters — a timesheet entry stores only the address's id
 * (`TimesheetEntryState.address`), so every entry pointing at this address keeps resolving to it and
 * simply sees the corrected fields, rather than being left pointing at a stale copy.
 */
export const editAddressRequested = createAction(
    '[Address] Edit requested',
    props<{ id: string; changes: AddressEdit; correlationId: string }>(),
);

/**
 * Dispatched by the facade's `hideAddress` with the id of the address to hide
 * (the UI model reference is resolved at the dispatch site). The id targets
 * exactly one address — addrKey cannot, since manually created addresses all
 * share the empty key. The reducer clears the address's
 * `visible` flag; the visibility persist effect then hands the updated
 * collection to `saveAddressesRequested` under the same `correlationId`.
 */
export const hideAddressRequested = createAction(
    '[Address] Hide requested',
    props<{ id: string; correlationId: string }>(),
);
export const unhideAddressRequested = createAction(
    '[Address] Unhide requested',
    props<{ id: string; correlationId: string }>(),
);

/**
 * Dispatched by the facade's `resetAddresses`. The reducer makes every address
 * visible again; the collection persist effect then hands the updated
 * collection to `saveAddressesRequested` under the same `correlationId`.
 */
export const resetAddressVisibility = createAction('[Address] Reset visibility', props<{ correlationId: string }>());

/**
 * Dispatched by the facade's `deleteAddress` with the id of the address to
 * delete. The reducer tombstones it — sets `deleted` rather than removing it from the
 * collection, so a timesheet entry that stores only the address's id (see
 * `TimesheetEntryState.address`) still resolves it — and hides it from the pickers
 * (`selectVisibleAddresses`). The collection persist effect then hands the updated
 * collection to `saveAddressesRequested` under the same `correlationId`.
 */
export const deleteAddressRequested = createAction(
    '[Address] Delete requested',
    props<{ id: string; correlationId: string }>(),
);

export const restoreAddressRequested = createAction(
    '[Address] Restore requested',
    props<{ id: string; correlationId: string }>(),
);

/** Dispatched by the facade's write methods; the save effect persists the collection. */
export const saveAddressesRequested = createAction(
    '[Address] Save requested',
    props<{ addresses: AddressState[]; correlationId: string }>(),
);

/**
 * Settle-only signal that the collection was persisted successfully (mapped from the generic
 * `slicePersisted`). The store is already the source of truth, so this carries no payload —
 * it only lets the facade's write bridge complete for the matching `correlationId`.
 */
export const addressesSaved = createAction('[Address] Saved', props<{ correlationId?: string }>());

export const addressStorageSyncError = createAction(
    '[Address] Storage sync error',
    props<{ error: string; correlationId?: string }>(),
);
